const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '/api/v1';

const buildUrl = (path) => {
  if (path.startsWith('http://') || path.startsWith('https://')) {
    return path;
  }
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return `${API_BASE_URL}${normalizedPath}`;
};

const createRequestId = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `req_${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
};

const parseResponse = async (response) => {
  const contentType = response.headers.get('content-type') || '';
  if (!contentType.includes('application/json')) {
    return null;
  }
  return response.json();
};

export const apiRequest = async (path, options = {}) => {
  const { method = 'GET', body, token, idempotencyKey } = options;
  const headers = {
    Accept: 'application/json',
    'X-Request-Id': createRequestId(),
  };

  if (body !== undefined && body !== null) {
    headers['Content-Type'] = 'application/json';
  }

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  if (idempotencyKey) {
    headers['Idempotency-Key'] = idempotencyKey;
  }

  const response = await fetch(buildUrl(path), {
    method,
    headers,
    body: body !== undefined && body !== null ? JSON.stringify(body) : undefined,
  });

  const data = await parseResponse(response);

  if (!response.ok) {
    const error = new Error('Request failed');
    error.status = response.status;
    error.data = data;
    throw error;
  }

  return data;
};

export const apiPostFormData = async (path, formData, token) => {
  const headers = {
    Accept: 'application/json',
    'X-Request-Id': createRequestId(),
  };
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const response = await fetch(buildUrl(path), {
    method: 'POST',
    headers,
    body: formData,
  });

  const data = await parseResponse(response);

  if (!response.ok) {
    const error = new Error('Request failed');
    error.status = response.status;
    error.data = data;
    throw error;
  }

  return data;
};

export const apiPost = (path, body, token, idempotencyKey) =>
  apiRequest(path, { method: 'POST', body, token, idempotencyKey });

export const apiGet = (path, token) => apiRequest(path, { method: 'GET', token });

export const apiPatch = (path, body, token) =>
  apiRequest(path, { method: 'PATCH', body, token });

/** Download a binary file and trigger browser save. */
export const apiDownloadFile = async (path, token, filename) => {
  const headers = { Accept: '*/*' };
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  const response = await fetch(buildUrl(path), { method: 'GET', headers });
  if (!response.ok) {
    const error = new Error('Request failed');
    error.status = response.status;
    try {
      error.data = await response.json();
    } catch {
      error.data = null;
    }
    throw error;
  }
  const blob = await response.blob();
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};
